import { useEffect, useState, type CSSProperties, type ReactNode } from "react";
import clsx from "clsx";
import { Settings, SlidersHorizontal, X } from "lucide-react";
import { ScriptControls } from "./ScriptControls";
import { VoiceInputPanel } from "./VoiceInputPanel";
import { energyColorForBatteryLevel, fuelColorForFuelLevel, MAX_FUEL_RANGE_KM, MAX_RANGE_KM } from "../../config/vehicleEnergy";
import type { RealtimeVoicePhase } from "../../hooks/useRealtimeVoiceGateway";
import type { VoiceCommandIntent } from "../../logic/voiceCommand";
import type { CabinState, DemoPreset } from "../../types/domain";

interface ControlDrawerProps {
  open: boolean;
  permanent?: boolean;
  state: CabinState;
  onChange: (patch: Partial<CabinState>) => void;
  onClose: () => void;
  onPreset: (preset: DemoPreset) => void;
  onReset: () => void;
  onSettings?: () => void;
  onTtsEnabledChange: (enabled: boolean) => void;
  onTtsStop: () => void;
  onManualVoiceCaptionChange: (text: string) => void;
  onManualVoiceCaptionSubmit: (text: string) => void;
  onVoiceTranscript: (transcript: string) => void;
  onRealtimeVoiceStart: () => void;
  onRealtimeVoiceStop: () => void;
  realtimeVoiceAudioListening: boolean;
  realtimeVoiceAudioSupported: boolean;
  realtimeVoiceConnected: boolean;
  realtimeVoiceError: string | null;
  realtimeVoicePhase: RealtimeVoicePhase;
  realtimeVoiceProvider: string;
  realtimeVoiceReplyText: string;
  realtimeVoiceTranscript: string;
  ttsEnabled: boolean;
  ttsSpeaking: boolean;
  ttsSupported: boolean;
  voiceFeedback: VoiceCommandIntent | null;
}

export function ControlDrawer({
  open,
  permanent = false,
  state,
  onChange,
  onClose,
  onPreset,
  onReset,
  onSettings,
  onTtsEnabledChange,
  onTtsStop,
  onManualVoiceCaptionChange,
  onManualVoiceCaptionSubmit,
  onVoiceTranscript,
  onRealtimeVoiceStart,
  onRealtimeVoiceStop,
  realtimeVoiceAudioListening,
  realtimeVoiceAudioSupported,
  realtimeVoiceConnected,
  realtimeVoiceError,
  realtimeVoicePhase,
  realtimeVoiceProvider,
  realtimeVoiceReplyText,
  realtimeVoiceTranscript,
  ttsEnabled,
  ttsSpeaking,
  ttsSupported,
  voiceFeedback
}: ControlDrawerProps) {
  const energyColor = energyColorForBatteryLevel(state.batteryLevel);
  const fuelColor = fuelColorForFuelLevel(state.fuelLevel);
  const [destinationDraft, setDestinationDraft] = useState(state.destinationName);

  useEffect(() => {
    setDestinationDraft(state.destinationName);
  }, [state.destinationName]);

  function commitDestinationDraft() {
    const nextDestinationName = destinationDraft.trim();

    if (!nextDestinationName) {
      setDestinationDraft(state.destinationName);
      return;
    }

    setDestinationDraft(nextDestinationName);

    if (nextDestinationName !== state.destinationName) {
      onChange({ destinationName: nextDestinationName });
    }
  }

  return (
    <aside className={clsx("control-drawer", open && "control-drawer--open", permanent && "control-drawer--permanent")}>
      <div className="control-drawer__header">
        <span><SlidersHorizontal size={18} /> 演示控制</span>
        <div className="control-drawer__header-actions">
          {onSettings ? (
            <button onClick={onSettings} aria-label="模型设置"><Settings size={18} /></button>
          ) : null}
          {!permanent ? (
            <button onClick={onClose} aria-label="关闭条件抽屉"><X size={19} /></button>
          ) : null}
        </div>
      </div>

      <div className="control-drawer__section">
        <h3>语音输入</h3>
        <VoiceInputPanel
          feedback={voiceFeedback}
          ttsEnabled={ttsEnabled}
          ttsSpeaking={ttsSpeaking}
          ttsSupported={ttsSupported}
          onManualVoiceCaptionChange={onManualVoiceCaptionChange}
          onManualVoiceCaptionSubmit={onManualVoiceCaptionSubmit}
          onTranscript={onVoiceTranscript}
          onRealtimeVoiceStart={onRealtimeVoiceStart}
          onRealtimeVoiceStop={onRealtimeVoiceStop}
          onTtsEnabledChange={onTtsEnabledChange}
          onTtsStop={onTtsStop}
          realtimeAudioListening={realtimeVoiceAudioListening}
          realtimeAudioSupported={realtimeVoiceAudioSupported}
          realtimeConnected={realtimeVoiceConnected}
          realtimeError={realtimeVoiceError}
          realtimePhase={realtimeVoicePhase}
          realtimeProvider={realtimeVoiceProvider}
          realtimeReplyText={realtimeVoiceReplyText}
          realtimeTranscript={realtimeVoiceTranscript}
        />
      </div>

      <div className="control-drawer__section">
        <h3>演示脚本</h3>
        <ScriptControls onPreset={onPreset} onReset={onReset} />
      </div>

      <DrawerDisclosure title="座舱条件">
        <label>
          <span>桌面</span>
          <select value={state.desktop} onChange={(event) => onChange({ desktop: event.target.value as CabinState["desktop"] })}>
            <option value="ai">AI 桌面</option>
            <option value="standard">常规桌面</option>
          </select>
        </label>
        <label>
          <span>日期</span>
          <select value={state.dayType} onChange={(event) => onChange({ dayType: event.target.value as CabinState["dayType"] })}>
            <option value="weekday">工作日</option>
            <option value="weekend">周末</option>
          </select>
        </label>
        <label>
          <span>时间</span>
          <select value={state.timeOfDay} onChange={(event) => onChange({ timeOfDay: event.target.value as CabinState["timeOfDay"] })}>
            <option value="morning">上午</option>
            <option value="commuteHome">下班回家</option>
            <option value="evening">晚上</option>
            <option value="night">夜间</option>
          </select>
        </label>
        <label>
          <span>天气</span>
          <select value={state.weather} onChange={(event) => onChange({ weather: event.target.value as CabinState["weather"] })}>
            <option value="sunny">晴</option>
            <option value="cloudy">多云</option>
            <option value="rainy">雨</option>
          </select>
        </label>
        <label>
          <span>当前位置</span>
          <select value={state.location} onChange={(event) => onChange({ location: event.target.value as CabinState["location"] })}>
            <option value="office">办公区</option>
            <option value="home">家</option>
            <option value="mall">商场</option>
            <option value="destination">目的地</option>
          </select>
        </label>
        <label>
          <span>目的地</span>
          <input
            type="text"
            value={destinationDraft}
            onBlur={commitDestinationDraft}
            onChange={(event) => setDestinationDraft(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                commitDestinationDraft();
              }
            }}
          />
        </label>
        <label>
          <span>路况</span>
          <select value={state.traffic} onChange={(event) => onChange({ traffic: event.target.value as CabinState["traffic"] })}>
            <option value="light">顺畅</option>
            <option value="medium">适中</option>
            <option value="heavy">拥堵</option>
          </select>
        </label>
        <Toggle label="已停车" checked={state.parked} onChange={(parked) => onChange({ parked })} />
      </DrawerDisclosure>

      <DrawerDisclosure title="车辆状态">
        <Range label="电量" value={state.batteryLevel} min={5} max={100} suffix="%" accentColor={energyColor} onChange={(batteryLevel) => onChange({ batteryLevel })} />
        <DerivedRange label="续航" value={state.rangeKm} max={MAX_RANGE_KM} suffix="km" color={energyColor} />
        <Range label="油量" value={state.fuelLevel} min={0} max={100} suffix="%" accentColor={fuelColor} onChange={(fuelLevel) => onChange({ fuelLevel })} />
        <DerivedRange label="燃油续航" value={state.fuelRangeKm} max={MAX_FUEL_RANGE_KM} suffix="km" color={fuelColor} />
        <Range label="脏污度" value={state.vehicleDirty} min={0} max={100} suffix="%" onChange={(vehicleDirty) => onChange({ vehicleDirty })} />
        <Range label="目的地距离" value={state.destinationDistanceKm} min={0} max={60} step={0.2} suffix="km" onChange={(destinationDistanceKm) => onChange({ destinationDistanceKm })} />
      </DrawerDisclosure>

      <DrawerDisclosure title="服务数据">
        <Toggle label="洗车优惠" checked={state.carwashCoupon} onChange={(carwashCoupon) => onChange({ carwashCoupon })} />
        <Toggle label="待取快递" checked={state.parcelPending} onChange={(parcelPending) => onChange({ parcelPending })} />
        <Toggle label="包裹绑定驿站" checked={state.parcelOnRoute} onChange={(parcelOnRoute) => onChange({ parcelOnRoute })} />
        <Toggle label="停车缴费码" checked={state.parkingQrDetected} onChange={(parkingQrDetected) => onChange({ parkingQrDetected })} />
        <Range label="未来好天气" value={state.forecastGoodDays} min={0} max={7} suffix="天" onChange={(forecastGoodDays) => onChange({ forecastGoodDays })} />
        <p className="control-drawer__hint">桩数与电价使用下方演示条件；地图仅提供站点位置。</p>
        <Range label="可用快充桩" value={state.chargerAvailability} min={0} max={12} suffix="个" onChange={(chargerAvailability) => onChange({ chargerAvailability })} />
        <Range label="充电价格" value={state.chargerPrice} min={0.6} max={2.2} step={0.02} suffix="元" onChange={(chargerPrice) => onChange({ chargerPrice })} />
        <Range label="停车空位" value={state.parkingAvailability} min={0} max={80} suffix="个" onChange={(parkingAvailability) => onChange({ parkingAvailability })} />
        <Range label="步行距离" value={state.parkingDistanceM} min={80} max={1200} suffix="m" onChange={(parkingDistanceM) => onChange({ parkingDistanceM })} />
      </DrawerDisclosure>
    </aside>
  );
}

function DrawerDisclosure({
  title,
  children
}: {
  title: string;
  children: ReactNode;
}) {
  return (
    <details className="control-drawer__section control-drawer__section--collapsible">
      <summary>
        <span>{title}</span>
      </summary>
      <div className="control-drawer__section-body">
        {children}
      </div>
    </details>
  );
}

function Range({
  label,
  value,
  min,
  max,
  step = 1,
  suffix,
  accentColor,
  onChange
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  step?: number;
  suffix: string;
  accentColor?: string;
  onChange: (value: number) => void;
}) {
  return (
    <label>
      <span>{label}</span>
      <input
        type="range"
        value={value}
        min={min}
        max={max}
        step={step}
        style={accentColor ? { accentColor } : undefined}
        onChange={(event) => onChange(Number(event.target.value))}
      />
      <b style={accentColor ? { color: accentColor } : undefined}>{Number.isInteger(value) ? value : value.toFixed(2)}{suffix}</b>
    </label>
  );
}

function DerivedRange({
  label,
  value,
  max,
  suffix,
  color
}: {
  label: string;
  value: number;
  max: number;
  suffix: string;
  color: string;
}) {
  const progress = Math.max(0, Math.min(100, (value / max) * 100));

  return (
    <div className="control-drawer__derived-range" aria-label={`${label} ${value}${suffix}，按最大续航 ${max}${suffix} 自动换算`}>
      <span>{label}</span>
      <div className="control-drawer__derived-track" aria-hidden="true" style={{ "--energy-color": color } as CSSProperties}>
        <i style={{ width: `${progress}%` }} />
      </div>
      <b style={{ color }}>{value}{suffix}</b>
    </div>
  );
}

function Toggle({
  label,
  checked,
  onChange
}: {
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <label className="control-drawer__toggle">
      <span>{label}</span>
      <input type="checkbox" checked={checked} onChange={(event) => onChange(event.target.checked)} />
    </label>
  );
}
